import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calculator, TrendUp, MagnifyingGlass, PencilSimple, Trash, DotsThreeVertical } from '@phosphor-icons/react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { DepartmentNav } from '../components/DepartmentNav';
import { AmazonDisclosure } from '../components/AmazonDisclosure';
import { UpgradeGate } from '../components/UpgradeGate';
import { PageWhy } from '../components/PageWhy';
import { SEO } from '../components/SEO';
import { RotateHint } from '../components/RotateHint';
import { useZones } from '../hooks/useZones';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

export const WeedControlPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['products', 'common', 'dashboard']);
  const [products, setProducts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showApplicationDialog, setShowApplicationDialog] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  
  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products;
    const search = productSearch.toLowerCase();
    return products.filter(p => 
      p.name?.toLowerCase().includes(search) ||
      p.active_ingredient?.toLowerCase().includes(search) ||
      p.manufacturer?.toLowerCase().includes(search) ||
      p.weed_type?.toLowerCase().includes(search)
    );
  }, [products, productSearch]);
  
  // Calculator state
  const [area, setArea] = useState('');
  const [areaUnit, setAreaUnit] = useState('sqft');
  const [applicationRate, setApplicationRate] = useState('');
  const [calculatedAmount, setCalculatedAmount] = useState(null);
  const [calculatedCost, setCalculatedCost] = useState(null);
  
  // Application form
  const [appArea, setAppArea] = useState('');
  const [appRate, setAppRate] = useState('');
  const [appNotes, setAppNotes] = useState('');
  const [appZoneId, setAppZoneId] = useState('');
  const [appPhotos, setAppPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [appMethod, setAppMethod] = useState('spray');
  const [appSpreaderSetting, setAppSpreaderSetting] = useState('');
  const zones = useZones();
  
  // Video guides state
  const [videoGuides, setVideoGuides] = useState([]);
  const [videoSeason, setVideoSeason] = useState('');

  useEffect(() => {
    fetchData();
    fetchVideoGuides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVideoGuides = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/video-guides/weed`);
      setVideoGuides(res.data?.videos || []);
      setVideoSeason(res.data?.season || '');
    } catch (error) {
      console.error('Error fetching video guides:', error);
    }
  };

  const fetchData = async () => {
    try {
      const productsRes = await axios.get(`${API_URL}/api/weed/products`, { withCredentials: true });
      setProducts(productsRes.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching weed products:', error);
      }
    }
    try {
      const applicationsRes = await axios.get(`${API_URL}/api/applications`, { withCredentials: true });
      const appLogs = applicationsRes.data?.logs || applicationsRes.data || [];
      setApplications(appLogs.filter(app => 
        app.product_type === 'Herbicide' || app.product_type === 'Pre-Emergent' || app.product_type === 'Post-Emergent'
      ));
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching applications:', error);
      }
    }
  };

  // Edit/Delete state
  const [editingApp, setEditingApp] = useState(null);
  const [editProductName, setEditProductName] = useState('');
  const [editRate, setEditRate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [deletingAppId, setDeletingAppId] = useState(null);

  const openEditDialog = (app) => {
    setEditingApp(app);
    setEditProductName(app.product_name || '');
    setEditRate(app.application_rate || '');
    setEditNotes(app.notes || '');
    setShowEditDialog(true);
  };

  const handleUpdateApp = async (e) => {
    e.preventDefault();
    if (!editingApp) return;
    try {
      await axios.put(`${API_URL}/api/applications/${editingApp.id}`, { product_name: editProductName, application_rate: editRate, notes: editNotes }, { withCredentials: true });
      toast.success('Application updated');
      setShowEditDialog(false);
      setEditingApp(null);
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const handleDeleteApp = async (appId) => {
    try {
      await axios.delete(`${API_URL}/api/applications/${appId}`, { withCredentials: true });
      toast.success('Application discarded');
      setDeletingAppId(null);
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const calculateApplication = () => {
    if (!selectedProduct || !area || !applicationRate) {
      toast.error('Please fill in all fields');
      return;
    }

    const areaSqFt = areaUnit === 'acres' ? parseFloat(area) * 43560 : parseFloat(area);
    const rate = parseFloat(applicationRate);
    
    // Calculate amount needed (rate is per 1000 sq ft)
    const amountNeeded = (areaSqFt / 1000) * rate;
    
    setCalculatedAmount(amountNeeded);
    
    if (selectedProduct.cost_per_unit) {
      const cost = amountNeeded * selectedProduct.cost_per_unit;
      setCalculatedCost(cost);
    } else {
      setCalculatedCost(null);
    }
  };

  const handleLogApplication = async (e) => {
    e.preventDefault();
    if (!selectedProduct) {
      toast.error('Please select a product');
      return;
    }

    try {
      const rateUnit = appMethod === 'granular' ? (selectedProduct.rate_unit || 'lbs') : (selectedProduct.rate_unit || 'oz');
      const res = await axios.post(
        `${API_URL}/api/applications`,
        {
          product_name: selectedProduct.name,
          product_type: selectedProduct.product_type,
          application_rate: `${appRate} ${rateUnit}/1000 sq ft on ${appArea} ${areaUnit}`,
          application_method: appMethod,
          spreader_setting: appMethod === 'granular' ? appSpreaderSetting || null : null,
          notes: appNotes,
          zone_id: appZoneId || null,
          zone_name: zones.find(z => z.id === appZoneId)?.name || null
        },
        { withCredentials: true }
      );
      
      // Upload photos if any were selected
      const appId = res.data?.id;
      if (appId && appPhotos.length > 0) {
        setUploadingPhoto(true);
        for (const photo of appPhotos) {
          const formData = new FormData();
          formData.append('file', photo);
          try {
            await axios.post(`${API_URL}/api/applications/${appId}/photo`, formData, {
              withCredentials: true,
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (photoErr) {
            console.error('Photo upload failed:', photoErr);
          }
        }
        setUploadingPhoto(false);
      }
      
      toast.success('Application logged successfully!');
      setShowApplicationDialog(false);
      setAppArea('');
      setAppRate('');
      setAppNotes('');
      setAppZoneId('');
      setAppPhotos([]);
      setAppMethod('spray');
      setAppSpreaderSetting('');
      fetchData();
    } catch (error) {
      toast.error('Failed to log application');
    }
  };

  return (
    <div data-testid="weed-control-page" className="min-h-screen bg-background">
      <SEO
        title="Weed Control Department"
        description="Track pre-emergent and post-emergent herbicide applications by GDD. Know exactly when crabgrass germinates and when to apply. Product recommendations, spray logs, and timing guidance."
        keywords="weed control GDD, pre-emergent timing, crabgrass GDD, when to apply pre-emergent, herbicide application tracker, post-emergent spray timing"
        path="/weed"
        imageAlt="TurfPulse Weed Control — GDD-based herbicide timing for your lawn"
      />
      <DepartmentNav />
      <UpgradeGate feature="weed_department" featureLabel="Weed Control Department">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Page Title */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold">🌿 Weed Control Department</h2>
            <p className="text-muted-foreground">GDD-based herbicide timing for crabgrass, Poa annua, and broadleaf weeds</p>
            <PageWhy testId="weed-page-why">Hit crabgrass and Poa before they emerge — using GDD instead of a calendar — so your lawn doesn't get overrun while you're still waiting on "spring."</PageWhy>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Applications</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{applications.length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Products Available</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{products.length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pre-Emergent</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">
                {products.filter(p => p.product_type === 'Pre-Emergent').length}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Post-Emergent</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {products.filter(p => p.product_type === 'Post-Emergent').length}
              </div>
            </CardContent>
          </Card>
        </div>

        <RotateHint />
        <Tabs defaultValue="calculator" className="w-full">
          <TabsList className="flex w-full overflow-x-auto scrollbar-hide gap-1 bg-muted p-1 rounded-lg" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
            <TabsTrigger value="calculator" className="flex-1 min-w-[130px] whitespace-nowrap text-xs sm:text-sm">Herbicide Calculator</TabsTrigger>
            <TabsTrigger value="products" className="flex-1 min-w-[110px] whitespace-nowrap text-xs sm:text-sm">Product Library</TabsTrigger>
            <TabsTrigger value="history" className="flex-1 min-w-[130px] whitespace-nowrap text-xs sm:text-sm">Application History</TabsTrigger>
            <TabsTrigger value="videos" data-testid="weed-videos-tab" className="flex-1 min-w-[100px] whitespace-nowrap text-xs sm:text-sm">Video Guides</TabsTrigger>
          </TabsList>

          {/* Calculator Tab */}
          <TabsContent value="calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator weight="duotone" size={24} className="text-primary" />
                  Herbicide Application Calculator
                </CardTitle>
                <CardDescription>
                  Calculate product amount and cost for your treatment area
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Label Disclaimer */}
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40" data-testid="weed-label-disclaimer">
                  <span className="text-amber-600 mt-0.5 flex-shrink-0 font-bold text-sm">!</span>
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    <strong>Always read and follow the product label.</strong> The label is the law. Application rates, turf tolerances, and restricted-use requirements vary by product. TurfPulse provides general guidance — defer to the manufacturer's label for exact instructions.
                  </p>
                </div>

                {/* Product Selection - Dropdown */}
                <div>
                  <Label>Select Herbicide Product</Label>
                  <Select
                    value={selectedProduct?.id || ''}
                    onValueChange={(val) => {
                      const prod = products.find(p => p.id === val);
                      setSelectedProduct(prod || null);
                    }}
                  >
                    <SelectTrigger className="mt-2" data-testid="weed-product-dropdown">
                      <SelectValue placeholder="Choose an herbicide product..." />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name} — {product.active_ingredient} ({product.product_type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Product Cards - Quick Visual Reference */}
                {products.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">Or tap to select:</Label>
                  <div className="grid md:grid-cols-3 gap-4 mt-2">
                    {products.map((product) => (
                      <div
                        key={product.id}
                        onClick={() => setSelectedProduct(product)}
                        data-testid={`weed-product-card-${product.id}`}
                        className={`
                          border rounded-lg p-4 cursor-pointer transition-all
                          ${selectedProduct?.id === product.id
                            ? 'border-primary bg-primary/5 ring-2 ring-primary'
                            : 'border-border hover:border-primary/50'
                          }
                        `}
                      >
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`text-xs px-2 py-1 rounded ${
                            product.product_type === 'Pre-Emergent' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          }`}>
                            {product.product_type}
                          </span>
                          {product.purchase_tier === 'consumer' && (
                            <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">DIY</span>
                          )}
                          {!product.purchase_tier && (
                            <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">Pro</span>
                          )}
                        </div>
                        <h4 className="font-bold text-sm">{product.name}</h4>
                        <p className="text-xs text-muted-foreground mt-1">{product.active_ingredient}</p>
                        <p className="text-xs text-muted-foreground">
                          {product.application_rate_min}-{product.application_rate_max} {product.rate_unit || 'oz'}/1000 sq ft
                        </p>
                        {/* Field Guide Correlation */}
                        {product.application_gdd_window && (
                          <div className="mt-2 pt-2 border-t border-border/50">
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 font-medium">
                              GDD Window: {product.application_gdd_window}
                            </span>
                          </div>
                        )}
                        {product.targets && product.targets.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`targets-${product.id}`}>
                            {product.targets.slice(0, 4).map((target, i) => (
                              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40">
                                {target}
                              </span>
                            ))}
                            {product.targets.length > 4 && (
                              <span className="text-[10px] px-1.5 py-0.5 text-muted-foreground">+{product.targets.length - 4} more</span>
                            )}
                          </div>
                        )}
                        {product.cost_per_unit && (
                          <div className="mt-2">
                            <p className="text-xs text-primary font-medium">
                              ${product.cost_per_unit.toFixed(2)} / {product.unit_size}
                            </p>
                            <p className="text-[10px] text-muted-foreground/70 italic">Price may vary</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {selectedProduct && (
                  <>
                    {/* Area Input */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="area">Treatment Area</Label>
                        <Input
                          id="area"
                          type="number"
                          step="0.01"
                          value={area}
                          onChange={(e) => setArea(e.target.value)}
                          placeholder="Enter area"
                        />
                      </div>
                      <div>
                        <Label htmlFor="area-unit">Unit</Label>
                        <Select value={areaUnit} onValueChange={setAreaUnit}>
                          <SelectTrigger id="area-unit">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sqft">Square Feet</SelectItem>
                            <SelectItem value="acres">Acres</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Application Rate */}
                    <div>
                      <Label htmlFor="rate">Application Rate ({selectedProduct.rate_unit || 'oz'} per 1000 sq ft)</Label>
                      <Input
                        id="rate"
                        type="number"
                        step="0.01"
                        value={applicationRate}
                        onChange={(e) => setApplicationRate(e.target.value)}
                        placeholder={`Recommended: ${selectedProduct.application_rate_min}-${selectedProduct.application_rate_max}`}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Recommended range: {selectedProduct.application_rate_min}-{selectedProduct.application_rate_max} {selectedProduct.rate_unit || 'oz'}/1000 sq ft
                      </p>
                    </div>

                    {/* Calculate Button */}
                    <Button onClick={calculateApplication} className="w-full" size="lg">
                      <Calculator weight="bold" size={20} className="mr-2" />
                      Calculate Amount & Cost
                    </Button>

                    {/* Results */}
                    {calculatedAmount !== null && (
                      <div className="bg-primary/5 border border-primary rounded-lg p-6 space-y-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Product Needed</p>
                          <p className="text-3xl font-bold text-primary">
                            {calculatedAmount.toFixed(2)} {selectedProduct.rate_unit || 'oz'}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            of {selectedProduct.name}
                          </p>
                        </div>
                        
                        {calculatedCost !== null && (
                          <div className="border-t border-border pt-4">
                            <p className="text-sm text-muted-foreground">Estimated Cost</p>
                            <p className="text-2xl font-bold text-primary">
                              ${calculatedCost.toFixed(2)}
                            </p>
                          </div>
                        )}

                        <Button 
                          onClick={() => {
                            setAppArea(area);
                            setAppRate(applicationRate);
                            setShowApplicationDialog(true);
                          }}
                          variant="outline"
                          className="w-full"
                        >
                          Log This Application
                        </Button>
                        {selectedProduct?.affiliate_link && (
                          <a href={selectedProduct.affiliate_link} target="_blank" rel="nofollow sponsored noopener noreferrer" className="block">
                            <Button variant="secondary" className="w-full" data-testid="calc-buy-amazon">
                              Buy {selectedProduct.name} on Amazon
                            </Button>
                          </a>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle>Herbicide Product Library</CardTitle>
                    <CardDescription>Pre-emergent and post-emergent herbicides for weed control</CardDescription>
                  </div>
                  <div className="relative w-full sm:w-64">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      placeholder="Search products..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="pl-10"
                      data-testid="product-search-input"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredProducts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {productSearch ? `No products found matching "${productSearch}"` : 'No products available'}
                  </div>
                ) : (
                <div className="space-y-4">
                  {filteredProducts.map((product) => (
                    <div key={product.id} className="border border-border rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs px-2 py-1 rounded ${
                              product.product_type === 'Pre-Emergent' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            }`}>
                              {product.product_type}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold">{product.name}</h3>
                          <p className="text-sm text-muted-foreground">{product.manufacturer}</p>
                        </div>
                        {product.cost_per_unit && (
                          <div className="text-right">
                            <p className="text-2xl font-bold text-primary">${product.cost_per_unit.toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">per {product.unit_size}</p>
                            <p className="text-[10px] text-muted-foreground/60 italic">Price may vary — check retailer</p>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-sm text-foreground mb-4">{product.description}</p>
                      
                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Active Ingredient</p>
                          <p className="font-medium">{product.active_ingredient}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Application Rate</p>
                          <p className="font-medium">
                            {product.application_rate_min}-{product.application_rate_max} {product.rate_unit || 'oz'}/1000 sq ft
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Target GDD</p>
                          <p className="font-medium">{product.target_gdd_interval} GDD</p>
                        </div>
                      </div>
                      
                      {/* Field Guide Correlation */}
                      {(product.targets?.length > 0 || product.application_gdd_window) && (
                        <div className="mt-3 pt-3 border-t border-border/50" data-testid={`library-fieldguide-${product.id}`}>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Field Guide Match</p>
                          {product.application_gdd_window && (
                            <span className="inline-block text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 font-medium mr-2 mb-1">
                              GDD: {product.application_gdd_window}
                            </span>
                          )}
                          {product.targets?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {product.targets.map((target, i) => (
                                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40">
                                  {target}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="mt-4 flex gap-2">
                        <Button 
                          onClick={() => {
                            setSelectedProduct(product);
                            document.querySelector('[value="calculator"]').click();
                          }}
                          variant="outline"
                          size="sm"
                        >
                          Use in Calculator
                        </Button>
                        {product.affiliate_link && (
                          <a href={product.affiliate_link} target="_blank" rel="nofollow sponsored noopener noreferrer">
                            <Button variant="secondary" size="sm" data-testid={`buy-amazon-${product.id}`}>
                              Buy on Amazon <span className="text-xs opacity-70">(paid link)</span>
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Application History</CardTitle>
                <CardDescription>Your herbicide application records</CardDescription>
              </CardHeader>
              <CardContent>
                {applications.length > 0 ? (
                  <div className="space-y-4">
                    {applications.map((app) => (
                      <div key={app.id} className="border border-border rounded-lg p-4" data-testid={`app-log-${app.id}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-bold">{app.product_name}</h4>
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${app.application_method === 'granular' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'}`} data-testid={`method-badge-${app.id}`}>
                                {(app.application_method || 'spray') === 'granular' ? 'Granular' : 'Spray'}
                              </span>
                              {app.zone_name && (
                                <span className="text-xs px-2 py-0.5 rounded bg-secondary text-secondary-foreground">{app.zone_name}</span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{app.application_rate}</p>
                            {app.spreader_setting && (
                              <p className="text-xs text-muted-foreground">Spreader: {app.spreader_setting}</p>
                            )}
                            {app.notes && (
                              <p className="text-sm text-muted-foreground mt-2">{app.notes}</p>
                            )}
                            {app.temperature_f && (
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground" data-testid={`weather-${app.id}`}>
                                <span>{Math.round(app.temperature_f)}°F</span>
                                {app.humidity_percent && <span>{app.humidity_percent}% humidity</span>}
                                {app.wind_speed_mph && <span>{app.wind_speed_mph} mph wind</span>}
                              </div>
                            )}
                          </div>
                          <div className="flex items-start gap-2">
                            <div className="text-right">
                              <p className="text-sm font-medium">{Math.round(app.gdd_at_application)} GDD</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(app.applied_at).toLocaleDateString()}
                              </p>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`app-menu-${app.id}`}>
                                  <DotsThreeVertical size={18} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditDialog(app)}>
                                  <PencilSimple size={16} className="mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setDeletingAppId(app.id)} className="text-red-600 focus:text-red-600">
                                  <Trash size={16} className="mr-2" /> Discard
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        {/* Photos */}
                        {app.photos && app.photos.length > 0 && (
                          <div className="flex gap-2 mt-3 overflow-x-auto" data-testid={`photos-${app.id}`}>
                            {app.photos.map((photo, i) => (
                              <a key={i} href={`${API_URL}/api/applications/photos/${photo.file_id}`} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                                <img src={`${API_URL}/api/applications/photos/${photo.file_id}`} alt={photo.filename} className="h-16 w-16 object-cover rounded border border-border" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <span className="text-6xl">🌿</span>
                    <p className="text-muted-foreground mt-4">No applications logged yet</p>
                    <Button onClick={() => setShowApplicationDialog(true)} className="mt-4">
                      Log First Application
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Video Guides Tab */}
          <TabsContent value="videos">
            <Card>
              <CardHeader>
                <CardTitle>Weed Control Video Guides</CardTitle>
                <CardDescription>
                  Curated tutorials from top lawn care experts
                  {videoSeason && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 capitalize">{videoSeason} picks</span>}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {videoGuides.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-6" data-testid="weed-video-grid">
                    {videoGuides.map((video) => (
                      <VideoCard
                        key={video.video_id}
                        title={video.title}
                        description={video.description}
                        videoId={video.youtube_video_id}
                        creator={video.creator}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No video guides available for this season. Check back soon!</p>
                )}
                <p className="text-xs text-muted-foreground mt-4">Videos rotate seasonally. Featuring content from Ron Henry, GCI Matt, and Lawn Insider.</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        <AmazonDisclosure />
      </div>

      {/* Application Dialog */}
      <Dialog open={showApplicationDialog} onOpenChange={setShowApplicationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Herbicide Application</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogApplication} className="space-y-4">
            {/* Application Method Toggle */}
            <div>
              <Label>Application Method</Label>
              <div className="flex gap-2 mt-1">
                <Button type="button" variant={appMethod === 'spray' ? 'default' : 'outline'} size="sm" className="flex-1" onClick={() => setAppMethod('spray')} data-testid="method-spray-btn">
                  Spray
                </Button>
                <Button type="button" variant={appMethod === 'granular' ? 'default' : 'outline'} size="sm" className="flex-1" onClick={() => setAppMethod('granular')} data-testid="method-granular-btn">
                  Granular
                </Button>
              </div>
            </div>

            <div>
              <Label>Product</Label>
              <Select 
                value={selectedProduct?.id} 
                onValueChange={(id) => setSelectedProduct(products.find(p => p.id === id))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} ({product.product_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Area</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={appArea}
                  onChange={(e) => setAppArea(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={areaUnit} onValueChange={setAreaUnit}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sqft">Sq Ft</SelectItem>
                    <SelectItem value="acres">Acres</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Rate ({appMethod === 'granular' ? 'lbs' : (selectedProduct?.rate_unit || 'oz')}/1000 sq ft)</Label>
              <Input
                type="number"
                step="0.01"
                value={appRate}
                onChange={(e) => setAppRate(e.target.value)}
                required
              />
            </div>

            {appMethod === 'granular' && (
              <div>
                <Label>Spreader Setting (optional)</Label>
                <Input
                  value={appSpreaderSetting}
                  onChange={(e) => setAppSpreaderSetting(e.target.value)}
                  placeholder="e.g. 3.5 on Scotts EdgeGuard"
                  data-testid="spreader-setting-input"
                />
              </div>
            )}
            
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={appNotes}
                onChange={(e) => setAppNotes(e.target.value)}
                placeholder="Target weeds, conditions, observations..."
              />
            </div>

            {zones.length > 0 && (
              <div>
                <Label>Zone</Label>
                <Select value={appZoneId} onValueChange={setAppZoneId}>
                  <SelectTrigger data-testid="weed-dialog-zone">
                    <SelectValue placeholder="Select zone (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.map(z => (
                      <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Photo Upload */}
            <div>
              <Label>Photos (optional)</Label>
              <div className="mt-1">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  data-testid="app-photo-input"
                  onChange={(e) => setAppPhotos(Array.from(e.target.files || []))}
                  className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                />
                {appPhotos.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">{appPhotos.length} photo{appPhotos.length > 1 ? 's' : ''} selected</p>
                )}
              </div>
            </div>
            
            <Button type="submit" className="w-full" disabled={uploadingPhoto} data-testid="app-submit-btn">
              {uploadingPhoto ? 'Uploading photos...' : 'Save Application'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Application Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent data-testid="edit-app-dialog">
          <DialogHeader><DialogTitle>Edit Application Log</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdateApp} className="space-y-4">
            <div><Label>Product Name</Label><Input data-testid="edit-product-name" value={editProductName} onChange={(e) => setEditProductName(e.target.value)} required /></div>
            <div><Label>Application Rate</Label><Input data-testid="edit-rate" value={editRate} onChange={(e) => setEditRate(e.target.value)} /></div>
            <div><Label>Notes</Label><Input data-testid="edit-notes" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-edit-btn">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deletingAppId} onOpenChange={() => setDeletingAppId(null)}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader><DialogTitle>Discard Application Log?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This will permanently remove this entry. This cannot be undone.</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeletingAppId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDeleteApp(deletingAppId)} data-testid="confirm-delete-btn">Discard Entry</Button>
          </div>
        </DialogContent>
      </Dialog>

      </UpgradeGate>
    </div>
  );
};


function VideoCard({ title, description, videoId, creator }) {
  return (
    <div className="rounded-lg border overflow-hidden" data-testid={`video-${videoId}`}>
      <div className="relative aspect-video bg-muted">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
          loading="lazy"
        />
      </div>
      <div className="p-3">
        <h4 className="font-semibold text-sm">{title}</h4>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
        {creator && <p className="text-xs font-medium text-green-700 dark:text-green-400 mt-1">{creator}</p>}
      </div>
    </div>
  );
}
